import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { CalendarIcon, Clock } from 'lucide-react';
import { format, differenceInDays } from 'date-fns';
import { cn } from '@/lib/utils';
import { useRentalLocations } from '@/hooks/useRentalLocations';
import { useCreateRentalBooking, calculateRentalPrice } from '@/hooks/useRentalBookings';
import { RentalVehicle } from '@/hooks/useRentalVehicles';
import { usePaymentMethods } from '@/hooks/usePaymentMethods';
import { PayPalCheckout } from './PayPalCheckout';
import { useNavigate } from 'react-router-dom';

const formSchema = z.object({
  customer_name: z.string().min(2, 'Name must be at least 2 characters'),
  email: z.string().email('Invalid email address'),
  phone: z.string().min(10, 'Phone number must be at least 10 digits'),
  pickup_date: z.date({ required_error: 'Pickup date is required' }),
  pickup_time: z.string().min(1, 'Pickup time is required'),
  return_date: z.date({ required_error: 'Return date is required' }),
  return_time: z.string().min(1, 'Return time is required'),
  pickup_location: z.string().min(1, 'Pickup location is required'),
  return_location: z.string().min(1, 'Return location is required'),
  driver_age: z.number().min(21, 'Driver must be at least 21 years old'),
  driver_license_number: z.string().min(5, 'License number is required'),
  additional_driver: z.boolean().default(false),
  insurance_type: z.enum(['basic', 'full', 'premium']),
  extras: z.array(z.string()).default([]),
  payment_method: z.enum(['cash', 'paypal', 'stripe']),
  remarks: z.string().optional(),
});

type FormData = z.infer<typeof formSchema>;

interface VehicleBookingFormProps {
  vehicle: RentalVehicle;
}

const INSURANCE_COSTS = {
  basic: 10,
  full: 25,
  premium: 50,
};

const EXTRAS = [
  { id: 'gps', label: 'GPS Navigation', cost: 10 },
  { id: 'child_seat', label: 'Child Seat', cost: 15 },
  { id: 'additional_driver', label: 'Additional Driver', cost: 20 },
  { id: 'wifi', label: 'WiFi Hotspot', cost: 15 },
];

export const VehicleBookingForm = ({ vehicle }: VehicleBookingFormProps) => {
  const navigate = useNavigate();
  const { data: locations } = useRentalLocations();
  const { paymentMethods } = usePaymentMethods();
  const createBooking = useCreateRentalBooking();
  const [showPayPal, setShowPayPal] = useState(false);
  const [bookingData, setBookingData] = useState<any>(null);

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      insurance_type: 'basic',
      additional_driver: false,
      extras: [],
      payment_method: 'cash',
    },
  });

  const watchedValues = form.watch();
  const pickupDate = watchedValues.pickup_date;
  const returnDate = watchedValues.return_date;
  const insuranceType = watchedValues.insurance_type;
  const selectedExtras = watchedValues.extras || [];

  const totalDays = pickupDate && returnDate ? differenceInDays(returnDate, pickupDate) : 0;
  const insuranceCost = totalDays * (INSURANCE_COSTS[insuranceType] || 0);
  const extrasCost = selectedExtras.reduce((sum, extraId) => {
    const extra = EXTRAS.find((e) => e.id === extraId);
    return sum + (extra ? extra.cost * totalDays : 0);
  }, 0);

  const totalPrice = totalDays > 0
    ? calculateRentalPrice(
        vehicle.daily_rate,
        vehicle.weekly_rate,
        vehicle.monthly_rate,
        totalDays,
        insuranceCost,
        extrasCost
      )
    : 0;

  const onSubmit = async (data: FormData) => {
    const booking = {
      vehicle_id: vehicle.id,
      vehicle_name: vehicle.vehicle_name,
      customer_name: data.customer_name,
      email: data.email,
      phone: data.phone,
      pickup_date: format(data.pickup_date, 'yyyy-MM-dd'),
      pickup_time: data.pickup_time,
      return_date: format(data.return_date, 'yyyy-MM-dd'),
      return_time: data.return_time,
      pickup_location: data.pickup_location,
      return_location: data.return_location,
      driver_age: data.driver_age,
      driver_license_number: data.driver_license_number,
      additional_driver: data.additional_driver,
      insurance_type: data.insurance_type,
      extras: data.extras,
      total_days: totalDays,
      daily_rate: Number(vehicle.daily_rate),
      extras_cost: Number(extrasCost),
      insurance_cost: Number(insuranceCost),
      total_price: Number(totalPrice),
      payment_method: data.payment_method,
      payment_status: 'pending' as const,
      payment_id: null,
      booking_status: 'pending' as const,
      remarks: data.remarks || null,
    };

    if (data.payment_method === 'paypal') {
      // Create booking first to get booking number for PayPal
      createBooking.mutate(booking, {
        onSuccess: (newBooking) => {
          setBookingData(newBooking);
          setShowPayPal(true);
        },
      });
    } else {
      createBooking.mutate(booking, {
        onSuccess: (newBooking) => {
          navigate(`/rental-booking-confirmation?booking=${newBooking.booking_number}`);
        },
      });
    }
  };

  if (showPayPal && bookingData) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Complete Payment</CardTitle>
        </CardHeader>
        <CardContent>
          <PayPalCheckout
            booking={bookingData}
            amount={bookingData.total_price.toString()}
            onSuccess={(details) => {
              console.log('Payment successful:', details);
              navigate(`/rental-booking-confirmation?booking=${bookingData.booking_number}`);
            }}
            onError={(error) => {
              console.error('Payment error:', error);
              setShowPayPal(false);
            }}
          />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Book This Vehicle</CardTitle>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            {/* Customer Information */}
            <div className="space-y-4">
              <h3 className="font-semibold text-lg">Customer Information</h3>
              <FormField
                control={form.control}
                name="customer_name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Full Name</FormLabel>
                    <FormControl>
                      <Input placeholder="John Doe" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email</FormLabel>
                      <FormControl>
                        <Input type="email" placeholder="john@example.com" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="phone"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Phone</FormLabel>
                      <FormControl>
                        <Input placeholder="+1234567890" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            {/* Rental Dates & Locations */}
            <div className="space-y-4">
              <h3 className="font-semibold text-lg">Rental Details</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="pickup_date"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Pickup Date</FormLabel>
                      <Popover>
                        <PopoverTrigger asChild>
                          <FormControl>
                            <Button
                              variant="outline"
                              className={cn('w-full pl-3 text-left font-normal', !field.value && 'text-muted-foreground')}
                            >
                              {field.value ? format(field.value, 'PPP') : <span>Pick a date</span>}
                              <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                            </Button>
                          </FormControl>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar mode="single" selected={field.value} onSelect={field.onChange} disabled={(date) => date < new Date()} />
                        </PopoverContent>
                      </Popover>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="pickup_time"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Pickup Time</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Clock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                          <Input type="time" className="pl-10" {...field} />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="return_date"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Return Date</FormLabel>
                      <Popover>
                        <PopoverTrigger asChild>
                          <FormControl>
                            <Button
                              variant="outline"
                              className={cn('w-full pl-3 text-left font-normal', !field.value && 'text-muted-foreground')}
                            >
                              {field.value ? format(field.value, 'PPP') : <span>Pick a date</span>}
                              <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                            </Button>
                          </FormControl>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar mode="single" selected={field.value} onSelect={field.onChange} disabled={(date) => date < new Date()} />
                        </PopoverContent>
                      </Popover>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="return_time"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Return Time</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Clock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                          <Input type="time" className="pl-10" {...field} />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="pickup_location"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Pickup Location</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select pickup location" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {locations?.map((location) => (
                            <SelectItem key={location.id} value={location.name}>
                              {location.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="return_location"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Return Location</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select return location" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {locations?.map((location) => (
                            <SelectItem key={location.id} value={location.name}>
                              {location.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            {/* Driver Information */}
            <div className="space-y-4">
              <h3 className="font-semibold text-lg">Driver Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="driver_age"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Driver Age</FormLabel>
                      <FormControl>
                        <Input type="number" {...field} onChange={(e) => field.onChange(parseInt(e.target.value))} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="driver_license_number"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>License Number</FormLabel>
                      <FormControl>
                        <Input placeholder="ABC123456" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            {/* Insurance & Extras */}
            <div className="space-y-4">
              <h3 className="font-semibold text-lg">Insurance & Extras</h3>
              <FormField
                control={form.control}
                name="insurance_type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Insurance Type</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select insurance" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="basic">Basic (${INSURANCE_COSTS.basic}/day)</SelectItem>
                        <SelectItem value="full">Full Coverage (${INSURANCE_COSTS.full}/day)</SelectItem>
                        <SelectItem value="premium">Premium (${INSURANCE_COSTS.premium}/day)</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="extras"
                render={() => (
                  <FormItem>
                    <FormLabel>Optional Extras</FormLabel>
                    <div className="space-y-2">
                      {EXTRAS.map((extra) => (
                        <FormField
                          key={extra.id}
                          control={form.control}
                          name="extras"
                          render={({ field }) => (
                            <FormItem className="flex items-center space-x-3 space-y-0">
                              <FormControl>
                                <Checkbox
                                  checked={field.value?.includes(extra.id)}
                                  onCheckedChange={(checked) => {
                                    return checked
                                      ? field.onChange([...field.value, extra.id])
                                      : field.onChange(field.value?.filter((value) => value !== extra.id));
                                  }}
                                />
                              </FormControl>
                              <FormLabel className="font-normal">
                                {extra.label} (+${extra.cost}/day)
                              </FormLabel>
                            </FormItem>
                          )}
                        />
                      ))}
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Payment Method */}
            <div className="space-y-4">
              <h3 className="font-semibold text-lg">Payment</h3>
              <FormField
                control={form.control}
                name="payment_method"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Payment Method</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select payment method" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {paymentMethods?.map((method) => (
                          <SelectItem key={method.id} value={method.method_type}>
                            {method.method_type === 'cash' && 'Cash Payment'}
                            {method.method_type === 'paypal' && 'PayPal'}
                            {method.method_type === 'stripe' && 'Credit/Debit Card'}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Remarks */}
            <FormField
              control={form.control}
              name="remarks"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Additional Remarks (Optional)</FormLabel>
                  <FormControl>
                    <Textarea placeholder="Any special requests or notes..." {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Price Summary */}
            {totalDays > 0 && (
              <div className="bg-muted p-4 rounded-lg space-y-2">
                <h3 className="font-semibold text-lg mb-3">Price Summary</h3>
                <div className="flex justify-between text-sm">
                  <span>Rental ({totalDays} days x ${vehicle.daily_rate})</span>
                  <span>${totalDays * vehicle.daily_rate}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>Insurance</span>
                  <span>${insuranceCost}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>Extras</span>
                  <span>${extrasCost}</span>
                </div>
                <div className="border-t pt-2 flex justify-between font-bold text-lg">
                  <span>Total</span>
                  <span className="text-primary">${totalPrice}</span>
                </div>
              </div>
            )}

            <Button type="submit" className="w-full" size="lg" disabled={createBooking.isPending || totalDays <= 0}>
              {createBooking.isPending ? 'Processing...' : `Book Now - $${totalPrice}`}
            </Button>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
};